// Partner cockpit — firm settings (B5B-PARTNER-COCKPIT §7). The firm's OWN settings: profile, certifications,
// tenant relationships, and consultant roster across all its engagements. Firm-admin only (403 otherwise).
// Reads the firm from the consultant's first firm (the §9 firm-switcher is a later refinement). Behind real auth.
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Building2, BadgeCheck } from "lucide-react";
import { cockpit, CockpitError } from "../lib/cockpit";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

function fmt(d: string | null): string { return d ? new Date(d).toLocaleDateString() : "—"; }

export function FirmSettingsRoute() {
  const firms = useQuery({ queryKey: ["cockpit-firms"], queryFn: () => cockpit.firms(), retry: false });
  const firmId = firms.data?.[0]?.firmId;
  const settings = useQuery({
    queryKey: ["cockpit-firm-settings", firmId],
    queryFn: () => cockpit.firmSettings(firmId!),
    enabled: !!firmId,
    retry: false,
  });

  const denied = settings.error instanceof CockpitError && settings.error.status === 403;
  const unauth = (firms.error instanceof CockpitError && firms.error.status === 401);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Firm settings" description={settings.data?.legalName ?? "Your consultancy firm"} />

      {unauth ? <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Sign in as a firm consultant.</CardContent></Card>
      : denied ? <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Only a firm administrator can manage firm settings.</CardContent></Card>
      : (firms.isLoading || settings.isLoading) ? <Skeleton className="h-40" />
      : settings.data ? (
        <>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Building2 className="size-4" /> {settings.data.legalName}</CardTitle>
              {settings.data.tradeName && <CardDescription>{settings.data.tradeName}</CardDescription>}</CardHeader>
            <CardContent className="flex flex-wrap gap-4 text-sm">
              {settings.data.certified && <Badge className="gap-1"><BadgeCheck className="size-3" /> 9-Sprints certified</Badge>}
              {settings.data.cadenceWeeks != null && <span className="text-muted-foreground">Sprint cadence: {settings.data.cadenceWeeks} weeks</span>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Tenant relationships <span className="text-muted-foreground">({settings.data.tenants.length})</span></CardTitle></CardHeader>
            <CardContent>
              {settings.data.tenants.length === 0 ? <p className="text-sm text-muted-foreground">No engagements.</p> : (
                <ul className="flex flex-col gap-2">{settings.data.tenants.map((t) => (
                  <li key={t.engagementId} className="flex items-center justify-between text-sm">
                    <Link to="/tenant/$tenantId" params={{ tenantId: t.tenantId }} className="hover:underline">{t.tenantName}</Link>
                    <span className="flex items-center gap-2"><Badge variant={t.status === "active" ? "default" : "secondary"}>{t.status}</Badge><span className="text-muted-foreground">{t.consultants} consultants</span></span>
                  </li>
                ))}</ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Consultants <span className="text-muted-foreground">({settings.data.consultants.filter((c) => !c.revoked).length})</span></CardTitle></CardHeader>
            <CardContent>
              <ul className="flex flex-col gap-2">{settings.data.consultants.filter((c) => !c.revoked).map((c) => (
                <li key={c.ipuId} className="flex items-center justify-between text-sm">
                  <span>{c.fullName || c.email}</span>
                  <span className="flex items-center gap-2"><Badge variant="outline">{c.role}</Badge><span className="text-muted-foreground">{c.tenantName}</span></span>
                </li>
              ))}</ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Certifications</CardTitle></CardHeader>
            <CardContent>
              {settings.data.certifications.length === 0 ? <p className="text-sm text-muted-foreground">No certifications recorded. Certification upload + counsel approval is a later step.</p> : (
                <ul className="flex flex-col gap-2">{settings.data.certifications.map((c, i) => (
                  <li key={i} className="flex items-center justify-between text-sm"><span>{c.kind}{c.issuer ? ` — ${c.issuer}` : ""}</span><span className="text-muted-foreground">exp {fmt(c.expiresAt)}</span></li>
                ))}</ul>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
